import { Box, Container, Heading, Text } from '@radix-ui/themes';
import { Breadcrumbs } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/shared/breadcrumbs';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/denversiteworks/contexts/use-tenant-context';
import { createRoute } from '@tanstack/react-router';
import { rootRoute } from '@trashlab/sitesapp/src/routes/__root';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

const PrivacyPolicyPage = () => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization.address as any);

  return (
    <Container size="4" p="8">
      <Breadcrumbs title="Privacy Policy & Text Messaging Terms" light />

      <Box className="space-y-8 py-8">
        <Box className="space-y-4">
          <Heading as="h1" size="6" className="text-blue-950">
            Text Messaging Terms & Privacy Policy
          </Heading>
          <Text as="p" size="2" className="text-slate-500">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })}
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Text Messaging Terms
          </Heading>

          <Box className="space-y-4">
            <Heading as="h3" size="4" className="text-blue-900">
              1. Program Description
            </Heading>
            <Text as="p" size="3" className="text-slate-600">
              By opting in, you agree to receive text messages and/or calls from{' '}
              {website?.organization.name} related to your waste services. This
              includes, but is not limited to:
              <ul className="list-disc pl-6 space-y-2">
                <li className="text-slate-600">
                  <Text size="3">Order confirmations</Text>
                </li>
                <li className="text-slate-600">
                  <Text size="3">Delivery updates</Text>
                </li>
                <li className="text-slate-600">
                  <Text size="3">Service reminders</Text>
                </li>
                <li className="text-slate-600">
                  <Text size="3">Important account notifications</Text>
                </li>
              </ul>
              Message frequency may vary based on your interactions and service
              activity. Message and data rates may apply. Consent is not a
              condition of purchase.
            </Text>
          </Box>

          <Box className="space-y-4">
            <Heading as="h3" size="4" className="text-blue-900">
              2. Opt-In Disclosure
            </Heading>
            <Text as="p" size="3" className="text-slate-600">
              <strong>Opt-In Language for Forms:</strong>
            </Text>
            <Text
              as="p"
              size="3"
              className="text-slate-600 italic bg-slate-50 p-4 rounded"
            >
              "By checking this box, I agree to receive text messages and/or
              calls from {website?.organization.name} regarding service
              reminders, order confirmations, and important account updates.
              Message frequency may vary. Message and data rates may apply.
              Consent is not a condition of purchase. Reply STOP to cancel, HELP
              for help."
            </Text>
            <Text as="p" size="3" className="text-slate-600">
              This language must be clearly visible next to an unchecked
              checkbox at the time of opt-in.
            </Text>
          </Box>

          <Box className="space-y-4">
            <Heading as="h3" size="4" className="text-blue-900">
              3. Opt-Out Instructions
            </Heading>
            <Text as="p" size="3" className="text-slate-600">
              You can cancel the SMS service at any time by replying "STOP".
              After you reply "STOP," we'll send a confirmation message and you
              will no longer receive SMS messages from us. To rejoin, just sign
              up again.
            </Text>
          </Box>

          <Box className="space-y-4">
            <Heading as="h3" size="4" className="text-blue-900">
              4. Support
            </Heading>
            <Text as="p" size="3" className="text-slate-600">
              For help, reply "HELP" or contact us at{' '}
              {website?.organization.billingEmail}.
            </Text>
          </Box>

          <Box className="space-y-4">
            <Heading as="h3" size="4" className="text-blue-900">
              5. Carrier Disclaimer
            </Heading>
            <Text as="p" size="3" className="text-slate-600">
              Wireless carriers are not liable for delayed or undelivered
              messages.
            </Text>
          </Box>

          <Box className="space-y-4">
            <Heading as="h3" size="4" className="text-blue-900">
              6. Message Frequency & Rates
            </Heading>
            <Text as="p" size="3" className="text-slate-600">
              Message and data rates may apply. Message frequency will vary
              depending on your services. Contact your wireless provider for
              more details.
            </Text>
          </Box>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Privacy Policy
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            {website?.organization.name} ("we", "our", or "us") is committed to
            protecting your personal information and your right to privacy. This
            Privacy Policy document contains types of information that is
            collected and recorded by {website?.organization.name} and how we
            use it. If you have additional questions or require more information
            about our Privacy Policy, do not hesitate to contact us at{' '}
            {website?.organization.billingEmail}.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            What We Collect
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We may collect personal information when you:
          </Text>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-slate-600">
              <Text size="3">Submit a contact form</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Request a quote</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Call or text us</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Sign up for services</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Browse our website</Text>
            </li>
          </ul>

          <Text as="p" size="3" className="text-slate-600 mt-4">
            This may include:
          </Text>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-slate-600">
              <Text size="3">
                <strong>Personal Identification Information:</strong> Full name,
                email address, phone number, and postal address.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                <strong>Payment Information:</strong> Credit/debit card details
                or payment account information.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                <strong>Service Request Details:</strong> Information about your
                specific service needs and requests.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                <strong>Usage Data:</strong> Information about how you use our
                website and services, including your IP address, browser type,
                and browsing behavior.
              </Text>
            </li>
          </ul>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            How We Use Your Information
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We use the information we collect in various ways, including to:
          </Text>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-slate-600">
              <Text size="3">
                Provide, operate, and maintain our website and services.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Communicate with you via SMS, phone, and email.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Send quotes, updates, and service notifications.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Process your transactions and manage your orders.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Improve, personalize, and expand our website and services.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Provide customer service and support.</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Understand and analyze how you use our website and services.
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Comply with legal obligations.</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Develop new products, services, features, and functionality.
              </Text>
            </li>
          </ul>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            SMS & Phone Communications
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            By providing your phone number, you agree to receive calls and text
            messages related to your service. Message and data rates may apply.
            We use secure platforms and service providers to send messages and
            ensure the protection of your communications.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Information Sharing
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We do not sell, trade, or rent your personal data to outside
            parties. We may share your information with:
          </Text>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-slate-600">
              <Text size="3">
                Service providers (CRM systems, texting platforms, payment
                processors, etc.)
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Website hosting partners and other parties who assist us in
                operating our website and conducting our business
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Contractors helping deliver services to you</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Legal authorities, if required by law</Text>
            </li>
          </ul>
          <Text as="p" size="3" className="text-slate-600 mt-4">
            All parties we share information with are required to keep this
            information confidential and use it only for the purposes specified.
          </Text>
          <Text as="p" size="3" className="text-slate-600 mt-4">
            <strong>Mobile Information Sharing:</strong> We do not share mobile
            information with third parties or affiliates for marketing or
            promotional purposes. Information sharing to subcontractors in
            support services, such as customer service, is permitted. All other
            use case categories exclude text messaging originator opt-in data
            and consent; this information will not be shared with any third
            parties.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Data Security
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We implement a variety of security measures to maintain the safety
            of your personal information, including encryption, access control,
            and secure servers. Your personal information is contained behind
            secured networks and is only accessible by a limited number of
            persons who have special access rights to such systems, and are
            required to keep the information confidential.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Your Rights
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            You have the right to:
          </Text>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-slate-600">
              <Text size="3">Access or correct your personal data</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Request deletion of your data</Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">
                Withdraw consent to communications (including SMS)
              </Text>
            </li>
            <li className="text-slate-600">
              <Text size="3">Opt-out of marketing communications</Text>
            </li>
          </ul>
          <Text as="p" size="3" className="text-slate-600 mt-4">
            To exercise any of these rights, please contact us using the
            information provided in the Contact Us section below.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Cookies
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We use cookies and similar tracking technologies to track the
            activity on our website and hold certain information. Cookies are
            files with a small amount of data that are commonly used as an
            anonymous unique identifier. These are sent to your browser from the
            website that you visit and are stored on your device's hard drive.
            You can instruct your browser to refuse all cookies or to indicate
            when a cookie is being sent. However, if you do not accept cookies,
            you may not be able to use some portions of our Service.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Third-Party Links
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            Occasionally, at our discretion, we may include or offer third-party
            products or services on our website. These third-party sites have
            separate and independent privacy policies. We therefore have no
            responsibility or liability for the content and activities of these
            linked sites.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Children's Privacy
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We do not knowingly collect any Personal Identifiable Information
            from children under the age of 13. If you think that your child
            provided this kind of information on our website, we strongly
            encourage you to contact us immediately, and we will do our best
            efforts to promptly remove such information from our records.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Changes to This Privacy Policy
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page.
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </Text>
        </Box>

        <Box className="space-y-4">
          <Heading as="h2" size="5" className="text-blue-950">
            Contact Us
          </Heading>
          <Text as="p" size="3" className="text-slate-600">
            If you have any questions or suggestions about our Privacy Policy,
            need to exercise your rights, or require assistance with our
            services, do not hesitate to contact us:
          </Text>
          <Box className="mt-4 space-y-2">
            <Text as="p" size="3" className="text-slate-600">
              {website?.organization.name}
            </Text>
            <Text as="p" size="3" className="text-slate-600">
              <strong>Email:</strong> {website?.organization.billingEmail}
            </Text>
            {website?.organization.billingPhoneNumber && (
              <Text as="p" size="3" className="text-slate-600">
                <strong>Phone:</strong>{' '}
                {parsePhoneNumberFromString(
                  website?.organization.billingPhoneNumber,
                )?.formatNational() ?? ''}
              </Text>
            )}
            {orgAddress && (
              <Text as="p" size="3" className="text-slate-600">
                <strong>Address:</strong>{' '}
                {orgAddress.streetAddressPartOne}{' '}
                {orgAddress.streetAddressPartTwo}{' '}
                {orgAddress.city}{' '}
                {orgAddress.state}{' '}
                {orgAddress.postalCode}
              </Text>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy-policy',
  component: PrivacyPolicyPage,
});

export default Route;
